import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Integration and Accumulation of Change",
  examWeight: "17–20%",
  focus: "Change, Limits, Integration",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Exploring Accumulations of Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Core Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Integration is essentially <strong>multiplication</strong> where one of the factors is changing.
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>Geometry:</strong> Area = Height <LatexText text="$\times$" /> Width.</p>
              <p className="text-slate-700"><strong>Calculus:</strong> If the "Height" (rate of change) varies, we cannot simply multiply. We must accumulate the area under the curve.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Area as Accumulation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The area between the graph of a rate function <LatexText text="$f'(x)$" /> and the x-axis represents the <strong>net change</strong> in the original function <LatexText text="$f(x)$" />.
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Example</h4>
                <p className="text-sm text-slate-700 mt-1">If you graph Velocity (<LatexText text="$v(t)$" />) vs. Time (<LatexText text="$t$" />), the area under the curve represents <strong>Distance/Displacement</strong>.</p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Units:</strong> The units of the area are the product of the axes: <LatexText text="$(\text{units of } y) \cdot (\text{units of } x)$" />.</p>
              <p className="text-slate-700 text-sm">Example: <LatexText text="$(\text{miles/hour}) \cdot (\text{hours}) = \text{miles}$" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: Approximating Areas with Riemann Sums",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Method</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Since we cannot calculate the area under curved shapes using basic geometry, we approximate using rectangles. These approximations are called <strong>Riemann Sums</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Riemann Sums</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">1. LRAM (Left Riemann Sum)</h4>
                <p className="text-sm text-slate-700 mt-1">The height of the rectangle is defined by the function value at the <em>left</em> endpoint of the subinterval.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">2. RRAM (Right Riemann Sum)</h4>
                <p className="text-sm text-slate-700 mt-1">The height is defined by the function value at the <em>right</em> endpoint.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">3. MRAM (Midpoint Riemann Sum)</h4>
                <p className="text-sm text-slate-700 mt-1">The height is defined by the function value at the <em>middle</em> of the subinterval.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Trapezoidal Sum</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Instead of rectangles, we use trapezoids connecting the left and right function values.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <LatexText text="$$\text{Area} = \frac{1}{2} \Delta x (y_1 + y_2)$$" />
              <p className="text-sm text-slate-700 mt-3"><em>Note: A trapezoidal sum is the average of LRAM and RRAM.</em></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Overestimate vs. Underestimate Behavior</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">LRAM</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>If <LatexText text="$f$" /> is <strong>Increasing</strong>: Underestimate.</li>
                  <li>If <LatexText text="$f$" /> is <strong>Decreasing</strong>: Overestimate.</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">RRAM</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li>If <LatexText text="$f$" /> is <strong>Increasing</strong>: Overestimate.</li>
                  <li>If <LatexText text="$f$" /> is <strong>Decreasing</strong>: Underestimate.</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Trapezoidal</h4>
                <p className="text-sm text-slate-700 mt-1">Depends on <strong>Concavity</strong>.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Concave Up:</strong> Overestimate (secant lines sit above the curve).</li>
                  <li><strong>Concave Down:</strong> Underestimate (secant lines sit below the curve).</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Riemann Sums, Summation Notation, and Definite Integral Notation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Definition of the Definite Integral</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The definite integral is the <strong>limit</strong> of a Riemann Sum as the number of rectangles (<LatexText text="$n$" />) approaches infinity (and the width <LatexText text="$\Delta x$" /> approaches 0).
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\lim_{n \to \infty} \sum_{i=1}^{n} f(x_i^*) \Delta x = \int_{a}^{b} f(x) \, dx$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Anatomy of an Integral</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <LatexText text="$$\int_{a}^{b} f(x) \, dx$$" />
            </div>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong><LatexText text="$\int$" />:</strong> Integral Sign (stylized "S" for Sum).</p>
              <p className="text-slate-700"><strong><LatexText text="$a$" />:</strong> Lower limit of integration.</p>
              <p className="text-slate-700"><strong><LatexText text="$b$" />:</strong> Upper limit of integration.</p>
              <p className="text-slate-700"><strong><LatexText text="$f(x)$" />:</strong> The Integrand (height of the rectangles).</p>
              <p className="text-slate-700"><strong><LatexText text="$dx$" />:</strong> The variable of integration/differential (width of the infinitely thin rectangles).</p>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> You must be able to recognize a summation written in "sigma notation" and translate it into a definite integral <LatexText text="$\int_a^b f(x) dx$" />. Look for the <LatexText text="$\frac{b-a}{n}$" /> term, which represents <LatexText text="$dx$" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: The Fundamental Theorem of Calculus (Part 1) and Accumulation Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Accumulation Functions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We can define a function <LatexText text="$g(x)$" /> as the area under a curve <LatexText text="$f(t)$" /> from a fixed point <LatexText text="$a$" /> to a variable point <LatexText text="$x$" />.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <LatexText text="$$g(x) = \int_{a}^{x} f(t) \, dt$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Fundamental Theorem of Calculus (Part 1)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$f$" /> is continuous on <LatexText text="$[a, b]$" />, then the function <LatexText text="$g$" /> defined above is differentiable, and:
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <LatexText text="$$g'(x) = \frac{d}{dx} \left[ \int_{a}^{x} f(t) \, dt \right] = f(x)$$" />
              <p className="text-sm text-slate-700 mt-3"><em>Simply put: The derivative of an accumulation function is the integrand.</em></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Chain Rule Version</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If the upper limit is a function <LatexText text="$u(x)$" /> rather than just <LatexText text="$x$" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\frac{d}{dx} \left[ \int_{a}^{u(x)} f(t) \, dt \right] = f(u(x)) \cdot u'(x)$$" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Interpreting the Behavior of Accumulation Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graph Analysis</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This topic connects Unit 6 back to Unit 5 (Analysis of Functions).
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$g(x) = \int_{0}^{x} f(t) \, dt$" />, then:
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><LatexText text="$g'(x) = f(x)$" /></p>
              <p className="text-slate-700"><LatexText text="$g''(x) = f'(x)$" /></p>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              You analyze the graph of <LatexText text="$g$" /> by looking at the graph of <LatexText text="$f$" />:
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong><LatexText text="$g$" /> is increasing</strong> when <LatexText text="$f$" /> is positive (area is accumulating).</p>
              <p className="text-slate-700"><strong><LatexText text="$g$" /> is decreasing</strong> when <LatexText text="$f$" /> is negative.</p>
              <p className="text-slate-700"><strong><LatexText text="$g$" /> has a relative extremum</strong> when <LatexText text="$f$" /> crosses the x-axis (changes sign).</p>
              <p className="text-slate-700"><strong><LatexText text="$g$" /> is concave up</strong> when <LatexText text="$f$" /> is increasing (<LatexText text="$f' > 0$" />).</p>
              <p className="text-slate-700"><strong><LatexText text="$g$" /> has a Point of Inflection</strong> when <LatexText text="$f$" /> changes from increasing to decreasing (peaks/valleys of <LatexText text="$f$" />).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Applying Properties of Definite Integrals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Geometric Interpretation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The definite integral represents <strong>signed area</strong>.
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>Area above x-axis:</strong> Positive.</p>
              <p className="text-slate-700"><strong>Area below x-axis:</strong> Negative.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Essential Algebraic Properties</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Zero Width</h4>
                <LatexText text="$$\int_{a}^{a} f(x) \, dx = 0$$" />
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Reversal</h4>
                <LatexText text="$$\int_{a}^{b} f(x) \, dx = -\int_{b}^{a} f(x) \, dx$$" />
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Additivity</h4>
                <LatexText text="$$\int_{a}^{c} f(x) \, dx = \int_{a}^{b} f(x) \, dx + \int_{b}^{c} f(x) \, dx$$" />
                <p className="text-sm text-slate-700 mt-2">(Splitting an integral into chunks)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Linearity</h4>
                <LatexText text="$$\int_{a}^{b} (k \cdot f(x) \pm g(x)) \, dx = k \int_{a}^{b} f(x) \, dx \pm \int_{a}^{b} g(x) \, dx$$" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-7",
      title: "Topic 6.7: The Fundamental Theorem of Calculus (Part 2) and Definite Integrals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Evaluation Theorem</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This provides the computational method to evaluate a definite integral without drawing rectangles.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$F$" /> is any antiderivative of <LatexText text="$f$" /> (meaning <LatexText text="$F' = f$" />), then:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\int_{a}^{b} f(x) \, dx = F(b) - F(a)$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Net Change Theorem (Total Change Theorem)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Rearranging the FTC gives us the most applied formula in AP Calculus:
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <LatexText text="$$F(b) = F(a) + \int_{a}^{b} f'(x) \, dx$$" />
              <LatexText text="$$\text{Final Amount} = \text{Initial Amount} + \text{Accumulated Change}$$" />
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> Always look for the phrase "Find the total amount..." or "Find the position at <LatexText text="$t=5$" />." You will almost certainly use this formula: <LatexText text="$\text{Pos}(5) = \text{Pos}(0) + \int_0^5 v(t) dt$" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-8",
      title: "Topic 6.8: Finding Antiderivatives and Indefinite Integrals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Notation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              An integral without bounds (limits of integration) is an <strong>Indefinite Integral</strong>. The result is a function (or family of functions), not a number.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <LatexText text="$$\int f(x) \, dx = F(x) + C$$" />
              <p className="text-sm text-slate-700 mt-3"><strong>Don't forget the <LatexText text="$+C$" />!</strong></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Basic Power Rule for Integration</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$\int x^n \, dx = \frac{x^{n+1}}{n+1} + C \quad (\text{for } n \neq -1)$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Special Cases (Memorize These)</h3>
            <div className="space-y-2">
              <p className="text-slate-700"><LatexText text="$\int \frac{1}{x} \, dx = \ln|x| + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int e^x \, dx = e^x + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int \sin x \, dx = -\cos x + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int \cos x \, dx = \sin x + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int \sec^2 x \, dx = \tan x + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int \frac{1}{1+x^2} \, dx = \arctan x + C$" /></p>
              <p className="text-slate-700"><LatexText text="$\int \frac{1}{\sqrt{1-x^2}} \, dx = \arcsin x + C$" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-9",
      title: "Topic 6.9: Integrating Using Substitution (U-Sub)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept (Reverse Chain Rule)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This method is used when the integrand is the result of a chain rule derivative.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Form: <LatexText text="$\int f(g(x)) g'(x) \, dx$" />
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Choose <LatexText text="$u$" /></h4>
                  <p className="text-sm text-slate-600 mt-1">Usually the "inside" function or the part with the higher power.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Differentiate</h4>
                  <p className="text-sm text-slate-600 mt-1">Find <LatexText text="$du = g'(x) dx$" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Solve for <LatexText text="$dx$" /></h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$dx = \frac{du}{g'(x)}$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Substitute</h4>
                  <p className="text-sm text-slate-600 mt-1">Rewrite the integral entirely in terms of <LatexText text="$u$" />. The <LatexText text="$x$" /> terms must cancel out.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Integrate</h4>
                  <p className="text-sm text-slate-600 mt-1">Use basic rules on <LatexText text="$u$" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">6</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Back-Substitute</h4>
                  <p className="text-sm text-slate-600 mt-1">Replace <LatexText text="$u$" /> with the original <LatexText text="$x$" /> expression (only for Indefinite Integrals).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">U-Sub with Definite Integrals (Change of Variables)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When performing U-Sub on a definite integral <LatexText text="$\int_a^b$" />, you must convert the limits of integration from <LatexText text="$x$" />-values to <LatexText text="$u$" />-values.
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Lower Limit:</strong> <LatexText text="$u(a)$" /></p>
              <p className="text-slate-700"><strong>Upper Limit:</strong> <LatexText text="$u(b)$" /></p>
              <p className="text-slate-700"><strong>Do not</strong> back-substitute to <LatexText text="$x$" /> at the end; simply evaluate using the new <LatexText text="$u$" />-bounds.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-10",
      title: "Topic 6.10: Integrating Functions Using Long Division and Completing the Square",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Algebraic Manipulation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes you cannot integrate a fraction immediately. You must manipulate it first.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Long Division</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Use this when the degree of the numerator is <LatexText text="$\ge$" /> the degree of the denominator.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Example:</strong> <LatexText text="$\int \frac{x^2}{x+1} \, dx$" /></p>
              <p className="text-sm text-slate-700 mt-2">Perform polynomial long division to rewrite as polynomial + remainder term.</p>
              <p className="text-sm text-slate-700 mt-2">Integrate the terms separately (the remainder term usually results in a natural log).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Completing the Square</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Use this when you have a quadratic in the denominator, usually under a square root or in a fraction that looks like arctan.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700"><strong>Example:</strong> <LatexText text="$\int \frac{1}{x^2 + 6x + 10} \, dx$" /></p>
              <p className="text-sm text-slate-700 mt-2">Complete the square in the denominator: <LatexText text="$\int \frac{1}{(x+3)^2 + 1} \, dx$" /></p>
              <p className="text-sm text-slate-700 mt-2">This is an arctan form with <LatexText text="$u = x+3$" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-11-bc",
      title: "Topic 6.11: Integration by Parts (BC ONLY)",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This topic is exclusive to AP Calculus BC.</p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept (Reverse Product Rule)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Integration by parts is the reverse of the Product Rule for differentiation.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <LatexText text="$$\int u \, dv = uv - \int v \, du$$" />
            </div>
          </section>

          <Mnemonic title="LIATE" content="Strategy for choosing u: Logarithmic → Inverse Trig → Algebraic → Trigonometric → Exponential. Choose u from earlier in the list (L first, E last)." />

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li>Choose <LatexText text="$u$" /> (use LIATE priority)</li>
              <li>Let <LatexText text="$dv$" /> be everything else including <LatexText text="$dx$" /></li>
              <li>Find <LatexText text="$du$" /> by differentiating <LatexText text="$u$" /></li>
              <li>Find <LatexText text="$v$" /> by integrating <LatexText text="$dv$" /></li>
              <li>Apply the formula: <LatexText text="$uv - \int v \, du$" /></li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Tabular Method</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A shortcut for repeated integration by parts, useful when one part is a polynomial (differentiates to zero) and the other is cyclic (sine, cosine, <LatexText text="$e^x$" />).
            </p>
            <p className="text-slate-700">
              One column differentiates <LatexText text="$u$" />, the other integrates <LatexText text="$dv$" />, with alternating signs (<LatexText text="$+, -, +, ...$" />).
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-12-bc",
      title: "Topic 6.12: Integrating Using Linear Partial Fractions (BC ONLY)",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This topic is exclusive to AP Calculus BC.</p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Used to integrate rational functions where the denominator factors into distinct linear factors (and degree of numerator {'<'} degree of denominator).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Factor the denominator</strong></li>
              <li><strong>Decompose the fraction:</strong>
                <div className="bg-slate-50 p-3 rounded mt-2">
                  <LatexText text="$\frac{P(x)}{(x-a)(x-b)} = \frac{A}{x-a} + \frac{B}{x-b}$" />
                </div>
              </li>
              <li><strong>Solve for constants</strong> <LatexText text="$A$" /> and <LatexText text="$B$" /> (Heaviside cover-up method or system of equations)</li>
              <li><strong>Integrate term by term</strong> (usually results in natural logs)</li>
            </ol>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
              <p className="text-sm text-slate-700"><strong>Result form:</strong> <LatexText text="$A \ln|x-a| + B \ln|x-b| + C$" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-13-bc",
      title: "Topic 6.13: Evaluating Improper Integrals (BC ONLY)",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This topic is exclusive to AP Calculus BC.</p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              An integral is <strong>improper</strong> if:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li>One or both limits of integration are infinite (<LatexText text="$\int_1^\infty$" />)</li>
              <li>The integrand has an infinite discontinuity (vertical asymptote) within the interval</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Evaluation Method</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Replace the "problematic" boundary with a variable and take the limit.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Infinite Interval</h4>
                <LatexText text="$$\int_a^\infty f(x) \, dx = \lim_{b \to \infty} \int_a^b f(x) \, dx$$" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Discontinuity at a</h4>
                <LatexText text="$$\int_a^b f(x) \, dx = \lim_{c \to a^+} \int_c^b f(x) \, dx$$" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Convergence vs. Divergence</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>If the limit exists and is finite, the integral <strong>converges</strong></li>
              <li>If the limit is <LatexText text="$\pm \infty$" /> or does not exist, the integral <strong>diverges</strong></li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-14-bc",
      title: "Topic 6.14: Selecting Techniques for Antidifferentiation (BC ONLY)",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This topic is exclusive to AP Calculus BC.</p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Decision Process</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When faced with an integral, follow this mental checklist:
            </p>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Simplify First:</strong> Can you distribute, split fractions, or use trig identities?</li>
              <li><strong>Basic Rules:</strong> Is it a standard power rule, trig, or exponential?</li>
              <li><strong>U-Substitution:</strong> Is the derivative of one part present in the other part?</li>
              <li><strong>BC Only Checks:</strong>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Is it a product? → <strong>Integration by Parts</strong></li>
                  <li>Is it a rational function with factorable denominator? → <strong>Partial Fractions</strong></li>
                  <li>Is it an improper rational function? → <strong>Long Division</strong></li>
                  <li>Is it a quadratic in the denominator? → <strong>Complete the Square (Arctan)</strong></li>
                </ul>
              </li>
            </ol>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit6Notes;